import { jitsuClient, JitsuClient } from "@jitsu/sdk-js";
import * as Sentry from "@sentry/react";

import type { VisiblePipelineStage } from "@/types/contract-family-pipeline";

const JITSU_HOST = "__JITSU_HOST__";
const JITSU_KEY = "__JITSU_KEY__";
const JITSU_ENABLED = "__JITSU_ENABLED__";

let jitsu: JitsuClient | null = null;
let initialized = false;
let lastIdentifiedUser: string | null = null;

export const contractFamilyStageOpened = (stage: VisiblePipelineStage) => {
  capture("contract_family_stage_opened", { stage });
};

export const contractFamilyStageRejected = (requestedStage: string, highestAvailableStage: VisiblePipelineStage) => {
  capture("contract_family_stage_rejected", { requestedStage, highestAvailableStage });
};

export const documentUpload = (documentName: string, documentSize: number) => {
  capture("document_upload", { documentName, documentSize });
};

export const searchFilesAndFolders = (query: string, parent: string | undefined, mode: string) => {
  capture("search", { query, parent, mode });
};

export const snapshotCompare = () => {
  capture("snapshot_compare");
};

export const snapshotCreateEvent = () => {
  capture("snapshot_create_event");
};

export const snapshotCreate = (template: string, documents?: string[]) => {
  capture("snapshot_create", { template, documents });
};

export const snapshotView = (name: string) => {
  capture("snapshot_view", { name });
};

export const showSummary = (name: string) => {
  capture("show_summary", { name });
};

export const folderExport = (fseId: string, folderName?: string) => {
  capture("export_summaries", { fseId, folderName });
};

export const search = (query: string) => {
  capture("search", { query });
};

export const snapshotCollectionExport = (collection?: string) => {
  capture("snapshot_collection_export", { collection });
};

export const promptEnter = (promptText: string) => {
  capture("prompt_enter", { promptText });
};

export const sharepointLogin = () => {
  capture("sharepoint_login");
};

export const sharepointSync = (filename: string) => {
  capture("sharepoint_sync", { filename });
};

export const identify = (user: string) => {
  if (user === lastIdentifiedUser) return;
  lastIdentifiedUser = user;
  try {
    jitsu?.id({ id: user, email: user });
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const snapshotSearch = (query: string) => {
  capture("snapshot_search", { query });
};

export const improvePromptSendToChat = (prompt: string) => {
  capture("improve_prompt_send_to_chat", { prompt });
};

export const twoFactorAuthentication = (user: string) => {
  capture("two_factor_authentication", { user });
};

export const folderCreate = (name: string) => {
  capture("folder_create", { name });
};

export const folderRename = (oldName: string, newName: string, fseId: string) => {
  capture("folder_rename", { oldName, newName, fseId });
};

export const folderRemove = (id: string, name: string) => {
  capture("folder_remove", { id, name });
};

export const folderUnwrap = (id: string, name: string, movedCount: number) => {
  capture("folder_unwrap", { id, name, movedCount });
};

export const documentRemove = (id: string, name: string) => {
  capture("document_remove", { id, name });
};

export const documentMove = (id: string, name: string, targetFolderName: string, targetFolderId: string) => {
  capture("document_move", { id, name, targetFolderName, targetFolderId });
};

export const improvePrompt = (text: string) => {
  capture("improve_prompt", { text });
};

export const addPromptToLibrary = (content?: string) => {
  capture("add_prompt_to_library", { content });
};

export const promptLibrarySendToChat = (name: string) => {
  capture("prompt_library_send_to_chat", { name });
};

export const snapshotExport = () => {
  capture("snapshot_export");
};

export const snapshotAnalysis = () => {
  capture("snapshot_analysis");
};

export const pageview = (url: string) => {
  capture("pageview", { url });
};

export const init = () => {
  if (typeof window === "undefined" || initialized) return;
  initialized = true;

  const jitsuConfigured =
    btoa(JITSU_HOST) !== "X19KSVRTVV9IT1NUX18=" &&
    btoa(JITSU_KEY) !== "X19KSVRTVV9LRVlfXw==" &&
    btoa(JITSU_ENABLED) === "dHJ1ZQ==";

  if (jitsuConfigured) {
    jitsu = jitsuClient({
      tracking_host: JITSU_HOST,
      key: JITSU_KEY,
      log_level: "ERROR",
    });
  }
};

export const logout = () => {
  capture("logout");
};

export const sessionTimeout = () => {
  capture("session_timeout");
};

export const evidenceShow = () => {
  capture("evidence_show");
};

export const loginSso = () => {
  capture("login_sso", { method: "sso" });
};

export const loginStandard = () => {
  capture("login_standard", { method: "standard" });
};

export const translateDocumentOpen = () => {
  capture("translate_document_open");
};

export const translateDocumentConfirm = (
  documentName: string,
  sourceLang: string,
  targetLang: string,
  isScan: boolean,
) => {
  capture("translate_document_confirm", { documentName, sourceLang, targetLang, isScan });
};

export const sessionTimeoutWarning = () => {
  capture("session_timeout_warning");
};

type AnalyticsProperties = Record<string, unknown>;

const capture = (eventName: string, params?: AnalyticsProperties | null | undefined) => {
  try {
    jitsu?.track(eventName, params ?? undefined);
  } catch (error) {
    Sentry.captureException(error);
  }
};
